import { useCallback, useEffect, useMemo, useState, type DragEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { GripVertical, Pencil, Plus, Search, Trash2 } from 'lucide-react'
import { adminApi } from '../api'
import { Btn, Card, TableSkeleton, inputClass } from '../ui'
import { IconAction, LogoThumb, StatusBadge, type ClientRow } from './clientsShared'

export default function ClientsPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<ClientRow[]>([])
  const [loading, setLoading] = useState(true)
  const [savingOrder, setSavingOrder] = useState(false)
  const [q, setQ] = useState('')
  const [dragId, setDragId] = useState<number | null>(null)
  const [overId, setOverId] = useState<number | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await adminApi<{ clients?: ClientRow[] }>('/api/admin/website/clients')
    setRows(data.clients || [])
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const query = q.trim().toLowerCase()
  const searching = query.length > 0

  const visible = useMemo(() => {
    if (!searching) return rows
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(query) ||
        (r.industry || '').toLowerCase().includes(query) ||
        (r.description || '').toLowerCase().includes(query)
    )
  }, [rows, query, searching])

  async function persistOrder(next: ClientRow[]) {
    setRows(next)
    setSavingOrder(true)
    const { res } = await adminApi('/api/admin/website/clients/reorder', {
      method: 'PUT',
      json: { ids: next.map((r) => r.id) },
    })
    setSavingOrder(false)
    if (!res.ok) load()
  }

  function reorder(fromId: number, toId: number) {
    if (fromId === toId || searching) return
    const from = rows.findIndex((r) => r.id === fromId)
    const to = rows.findIndex((r) => r.id === toId)
    if (from < 0 || to < 0) return
    const next = [...rows]
    const [item] = next.splice(from, 1)
    next.splice(to, 0, item)
    void persistOrder(next.map((r, i) => ({ ...r, sort_order: i + 1 })))
  }

  function onDragStart(e: DragEvent, id: number) {
    if (searching) {
      e.preventDefault()
      return
    }
    setDragId(id)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', String(id))
  }

  function onDragOver(e: DragEvent, id: number) {
    if (searching || dragId == null) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (overId !== id) setOverId(id)
  }

  function onDrop(e: DragEvent, id: number) {
    e.preventDefault()
    const from = Number(e.dataTransfer.getData('text/plain') || dragId)
    if (from) reorder(from, id)
    setDragId(null)
    setOverId(null)
  }

  function onDragEnd() {
    setDragId(null)
    setOverId(null)
  }

  async function remove(id: number) {
    if (!confirm('Delete this client?')) return
    await adminApi(`/api/admin/website/clients/${id}`, { method: 'DELETE' })
    load()
  }

  return (
    <div className="space-y-4 sm:space-y-5">
      <Card className="!p-2.5 sm:!p-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, industry…"
              className={`${inputClass} !pl-9 !pr-3 !py-2 h-10 sm:h-11`}
            />
          </div>
          <Btn onClick={() => navigate('/admin/clients/new')}>
            <Plus className="h-4 w-4" aria-hidden />
            <span className="hidden sm:inline">Add</span>
          </Btn>
        </div>
        <p className="mt-2 text-xs text-slate-500 px-0.5">
          {loading
            ? 'Loading…'
            : searching
              ? `${visible.length} match${visible.length === 1 ? '' : 'es'} · clear search to reorder`
              : savingOrder
                ? 'Saving order…'
                : `${rows.length} clients · drag handle to reorder`}
        </p>
      </Card>

      {loading ? (
        <TableSkeleton rows={8} cols={4} />
      ) : (
        <Card className="!p-0 overflow-hidden">
          <ul className="divide-y divide-primary/5">
            {visible.map((r, idx) => {
              const dragging = dragId === r.id
              const over = overId === r.id && dragId !== r.id
              return (
                <li
                  key={r.id}
                  onDragOver={(e) => onDragOver(e, r.id)}
                  onDrop={(e) => onDrop(e, r.id)}
                  className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-3 transition ${
                    dragging ? 'opacity-40 bg-primary/[0.04]' : ''
                  } ${over ? 'bg-primary/[0.06] ring-1 ring-inset ring-primary/20' : 'hover:bg-primary/[0.03]'}`}
                >
                  <button
                    type="button"
                    draggable={!searching}
                    onDragStart={(e) => onDragStart(e, r.id)}
                    onDragEnd={onDragEnd}
                    disabled={searching}
                    title={searching ? 'Clear search to reorder' : 'Drag to reorder'}
                    aria-label="Drag to reorder"
                    className={`shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-xl border border-primary/10 ${
                      searching
                        ? 'cursor-not-allowed text-slate-300'
                        : 'cursor-grab active:cursor-grabbing text-slate-500 hover:bg-white hover:text-primary'
                    }`}
                  >
                    <GripVertical className="h-4 w-4" />
                  </button>

                  <span className="hidden sm:inline w-7 shrink-0 text-center text-xs font-bold tabular-nums text-slate-400">
                    {searching ? idx + 1 : r.sort_order || idx + 1}
                  </span>

                  <Link
                    to={`/admin/clients/${r.id}/edit`}
                    className="flex min-w-0 flex-1 items-center gap-3 group"
                  >
                    <LogoThumb path={r.logo_path} name={r.name} />
                    <div className="min-w-0">
                      <div className="font-semibold text-navy-900 truncate group-hover:text-primary">
                        {r.name}
                      </div>
                      <div className="text-xs text-slate-400 truncate">{r.industry || '—'}</div>
                    </div>
                  </Link>

                  <div className="hidden sm:block shrink-0">
                    <StatusBadge active={!!r.is_active} />
                  </div>

                  <div className="flex shrink-0 gap-1.5">
                    <IconAction label="Edit" onClick={() => navigate(`/admin/clients/${r.id}/edit`)}>
                      <Pencil className="h-4 w-4" />
                    </IconAction>
                    <IconAction label="Delete" danger onClick={() => remove(r.id)}>
                      <Trash2 className="h-4 w-4" />
                    </IconAction>
                  </div>
                </li>
              )
            })}
            {visible.length === 0 && (
              <li className="px-4 py-12 text-center text-sm text-slate-500">No clients found.</li>
            )}
          </ul>
        </Card>
      )}
    </div>
  )
}
